import React from "react";
import type { Locale } from "./i18n";

export class Formatter {
  readonly locale: Locale;

  constructor({ locale }: { locale: Formatter["locale"] }) {
    this.locale = locale;
  }

  parseDate(value?: string | Date | null): Date | null {
    if (!value) {
      return null;
    }
    if (typeof value !== "string") {
      return value;
    }
    const unix = Date.parse(value);
    if (isNaN(unix)) {
      return null;
    }
    return new Date(unix);
  }

  date(
    value: Date | string,
    options?: Intl.DateTimeFormatOptions,
  ): string | null {
    const date = this.parseDate(value);

    if (!date) {
      return null;
    }

    const { day = "numeric", month = "long", year = "numeric" } = options || {};

    return new Intl.DateTimeFormat(this.locale, {
      day,
      month,
      year,
    }).format(date);
  }

  numeralDate(value: Date | string): string | null {
    const date = this.parseDate(value);

    if (!date) {
      return null;
    }

    return new Intl.DateTimeFormat(this.locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  }

  currency(value: number | string): string {
    return new Intl.NumberFormat(this.locale, {
      style: "currency",
      currency: "EUR",
      currencyDisplay: "code",
    }).format(Number(value));
  }

  percentage(value: number): string {
    return new Intl.NumberFormat(this.locale, {
      style: "percent",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(value);
  }

  roundPercentage(value: number): string {
    return new Intl.NumberFormat(this.locale, {
      style: "percent",
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(value);
  }

  parseText(value: string | null | undefined): React.ReactNode {
    if (!value) return null;

    // Split by newlines to create paragraphs
    const paragraphs = value.split("\\n").filter((line) => line.trim() !== "");

    return (
      <>
        {paragraphs.map((paragraph, index) => {
          // Parse bold text within each paragraph
          const parts = paragraph.split(/(<b>.*?<\/b>)/g);

          const parsedContent = parts.map((part, partIndex) => {
            if (part.startsWith("<b>") && part.endsWith("</b>")) {
              // Extract text between <b> tags and convert to <strong>
              const boldText = part.slice(3, -4);
              return <strong key={partIndex}>{boldText}</strong>;
            }
            return part;
          });

          return <p key={index}>{parsedContent}</p>;
        })}
      </>
    );
  }
}
